import { addMonths, format, isAfter, parseISO, subMonths } from 'date-fns';
import * as yup from 'yup';

export const MAX_EXPORT_RANGE_MONTHS = 6;

export const getMaxExportDate = () => format(new Date(), 'yyyy-MM-dd');

const isNotFutureDate = (value?: string) => !value || value <= getMaxExportDate();

const toDateInputValue = (date: Date) => format(date, 'yyyy-MM-dd');

const emptyToUndefined = (value?: string) => (value?.length ? value : undefined);

export const getStartDateBounds = (endDateArg?: string) => {
	const endDate = emptyToUndefined(endDateArg);

	return {
		min: endDate ? toDateInputValue(subMonths(parseISO(endDate), MAX_EXPORT_RANGE_MONTHS)) : undefined,
		max: endDate ?? getMaxExportDate(),
	};
};

export const getEndDateBounds = (startDate?: string) => {
	const today = getMaxExportDate();

	if (!startDate) return { min: undefined, max: today };

	const rangeLimit = toDateInputValue(addMonths(parseISO(startDate), MAX_EXPORT_RANGE_MONTHS));

	return { min: startDate, max: rangeLimit < today ? rangeLimit : today };
};

export const FileExportSchema = yup.object({
	exportStartDate: yup.string().required().test('not-future', 'Start date cannot be in the future', isNotFutureDate),
	exportEndDate: yup
		.string()
		.required()
		.test('not-future', 'End date cannot be in the future', isNotFutureDate)
		.test('after-start', 'End date cannot be before the start date', (value, context) => {
			const { exportStartDate } = context.parent as { exportStartDate?: string };

			return !value || !exportStartDate || value >= exportStartDate;
		})
		.test('max-range', `Date range cannot exceed ${MAX_EXPORT_RANGE_MONTHS} months`, (value, context) => {
			const { exportStartDate } = context.parent as { exportStartDate?: string };

			if (!value || !exportStartDate) return true;

			return !isAfter(parseISO(value), addMonths(parseISO(exportStartDate), MAX_EXPORT_RANGE_MONTHS));
		}),
});

export type FileExportSchemaType = yup.InferType<typeof FileExportSchema>;

const BLOCKING_REASONS: Record<string, string> = {
	'not-future': 'Dates cannot be in the future.',
	'after-start': 'The end date must be on or after the start date.',
	'max-range': `Date range cannot exceed ${MAX_EXPORT_RANGE_MONTHS} months.`,
};

export const getExportBlockingReason = (errorTypes: (string | undefined)[]): string | undefined =>
	errorTypes.map((type) => (type ? BLOCKING_REASONS[type] : undefined)).find(Boolean);
